import React from "react";
import createDOMPurify from "dompurify";
import { BRIEF_SECTIONS } from "@/constants/brief-sections";
import {
  Frame,
  FrameHeader,
  FrameTitle,
  FramePanel,
} from "@/components/ui/frame";

// The "Empty" button in the guided editor stores a dashes-only sentinel.
const EMPTY_SENTINEL = /^-+$/;

function hasContent(v: unknown): v is string {
  return (
    typeof v === "string" &&
    v.trim().length > 0 &&
    !EMPTY_SENTINEL.test(v.trim())
  );
}

/** Read-only render of a campaign brief (guided sections or freeform HTML). */
export default function ShareBriefBody({
  brief,
}: {
  brief: Record<string, any>;
}) {
  const isFreeform = brief?.mode === "freeform";
  const visibleSections = BRIEF_SECTIONS.filter((s) => hasContent(brief?.[s.id]));
  const DOMPurify = React.useMemo(() => createDOMPurify(window), []);
  return (
    <div className="flex flex-col w-full max-w-4xl mx-auto p-5 gap-4">
      {hasContent(brief?.brief_title) && (
        <h1 className="text-xl font-semibold">{brief.brief_title}</h1>
      )}

      {isFreeform ? (
        hasContent(brief?.brief_content) ? (
          <Frame>
            <FramePanel>
              <div
                className="text-sm leading-relaxed [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_a]:underline [&_p]:mb-2"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(brief.brief_content),
                }}
              />
            </FramePanel>
          </Frame>
        ) : (
          <p className="text-sm text-muted-foreground">No brief content.</p>
        )
      ) : visibleSections.length ? (
        visibleSections.map((section) => (
          <Frame key={section.id}>
            <FrameHeader>
              <FrameTitle>{section.title}</FrameTitle>
            </FrameHeader>
            <FramePanel>
              <div className="text-sm leading-relaxed whitespace-pre-wrap">
                {brief[section.id]}
              </div>
            </FramePanel>
          </Frame>
        ))
      ) : (
        <p className="text-sm text-muted-foreground">No brief content.</p>
      )}
    </div>
  );
}
